import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './Results.css'
import ResultsDisplay from './ResultsDisplay/ResultsDisplay'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { DefaultState } from '../../types'
import { getAllResults } from '../../redux/slices'
import { joiResolver } from '@hookform/resolvers/joi'
import Joi from 'joi'
import { useForm } from 'react-hook-form'
import { Container, Row, Col, Form, FormGroup, Label } from 'reactstrap'
import ICSButton from '../../components/ICS/Button/ICSButton'
import ComponentBoxFooter from '../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSDateBox from '../../components/ICSDateBox/ICSDateBox'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ICSCheckBoxInput from '../../components/ICSCheckBoxInput/ICSCheckBoxInput'
import { AppDispatch } from '../../services/lib'

const schema = Joi.alternatives().try(
  Joi.object().keys({
    start: Joi.date().iso().allow(null),
    end: Joi.date().iso(),
    hasResults: Joi.boolean(),
  }),
  Joi.object().keys({
    start: Joi.date().iso(),
    end: Joi.date().iso().allow(null),
    hasResults: Joi.boolean(),
  }),
  Joi.object().keys({
    start: Joi.date().iso().allow(null),
    end: Joi.date().iso().allow(null),
    hasResults: Joi.boolean().invalid(false),
  })
)

const Results = () => {
  const formName = 'resultsPage'

  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    setValue,
    getValues,
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema),
  })

  const dispatch: AppDispatch = useDispatch()
  const formData = useSelector(
    (state: DefaultState) => state.forms && state.forms[formName]
  )

  useEffect(() => {
    if (formData) {
      Object.entries(formData).forEach(entry => {
        setValue(entry[0], entry[1], {
          shouldDirty: true,
          shouldValidate: true,
        })
      })
    }

    const initialState = async () => {
      dispatch(getAllResults())
    }

    initialState()
  }, [formData, setValue, dispatch])

  const onSearch = async () => {
    const { start, end, hasResults } = getValues()

    const queryObj: { [k: string]: string | number } = {
      start: start ? encodeURIComponent(start) : 0,
      end: end ? encodeURIComponent(end) : 0,
      hasResults,
    }

    dispatch(getAllResults(queryObj))

    return
  }

  return (
    <Container fluid={true}>
      <Row>
        <Col className='wivi-col'>
          <HeaderBar
            origin='IPA'
            routes={[
              {
                title: 'Results',
              },
            ]}
            help={{title: 'Results Help',text:
        <>
          <p>
            {' '}
            The results page is mostly equivalent to the data archive page,
            except instead of storing collections to search through, analysis
            reports are stored.{' '}
          </p>

          <p>
            {' '}
            Clicking Search without any parameters specified will display all
            user and shared results. You can specify one or the other by using
            the associated radio buttons.{' '}
          </p>

          <p>
            {' '}
            A result file that is shared will be displayed as a link. Clicking
            on it will show the user who shared the file with you. Files can be
            shared either manually on the Results page by selecting files and
            clicking on the "[x] Files Selected” dropdown and selecting Share
            Selections, or automatically by specifying users to share with in
            the Analyzer.{' '}
          </p>

          <p>
            {' '}
            Files can also be deleted or downloaded through the “Files Selected”
            dropdown.{' '}
          </p>

          <p>
            {' '}
            Select file types can also be “quick viewed” in the browser, or if
            appropriate, viewed externally in Dataspy.{' '}
          </p>
          </>
      }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={{ size: 12 }} className='wivi-col'>
          <ComponentBox isCustom={true} cardBodyId='resultsCardBody' cardClass='card-container' title='Search' accordion={true}>
            <Form onSubmit={handleSubmit(onSearch)}>
              <FormGroup>
                <Row>
                  <Col>
                    <Label className='date-range-label'>Date Range</Label>
                    <ICSDateBox
                      name='start'
                      control={control}
                      errors={errors}
                      placeholder='Start Date...'
                    />
                  </Col>
                  <Col>
                    <label className='date-range-label label-hidden'>
                      Hidden
                    </label>
                    <ICSDateBox
                      name='end'
                      control={control}
                      errors={errors}
                      placeholder='End Date...'
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className='has-results-checkbox'>
                      <ICSCheckBoxInput
                        name='hasResults'
                        control={control}
                        errors={errors}
                        label='Has Results'
                      />
                    </div>
                  </Col>
                </Row>
                <ComponentBoxFooter>
                  <ICSButton
                    id='formSubmit'
                    width={70}
                    useSubmitBehavior={true}
                    type='success'
                    disabled={!isValid}
                  >
                    Search
                  </ICSButton>
                </ComponentBoxFooter>
              </FormGroup>
            </Form>
          </ComponentBox>
        </Col>
      </Row>
      <Row>
        <Col xs={{ size: 12 }} className='wivi-col'>
          <ComponentBox>
            <ResultsDisplay />
          </ComponentBox>
        </Col>
      </Row>
    </Container>
  )
}

export default Results
